'use client';

import { forwardRef, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { Heading } from '../heading';
import { Slot, Slottable } from '../slot';
import { ToggleCardRootContext } from './context/toggle-card-root.context';
import { toggleCardHeaderRecipe } from './styles/toggle-card-header.styles';
import type { ToggleCardHeaderPropsInterface } from './types/toggle-card-header.types';

/**
 * @since 10.0.0
 */
export const ToggleCardHeader = forwardRef<HTMLDivElement, ToggleCardHeaderPropsInterface>(
  (props: ToggleCardHeaderPropsInterface, forwardedRef) => {
    const headerId = useId();
    const {
      asChild = false,
      className,
      children,
      css: cssProp,
      divider = true,
      id = headerId,
      ...rest
    } = props;
    const styles = css(toggleCardHeaderRecipe.raw({ divider }), cssProp);
    const Component = asChild ? Slot : 'div';
    const { setLabelId } = useContext(ToggleCardRootContext);

    useIsomorphicLayoutEffect(() => {
      setLabelId(id);
      return () => setLabelId(undefined);
    }, [id, setLabelId]);

    return (
      <Component
        ref={forwardedRef}
        id={id}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="ToggleCard.Header"
        className={cx('c11n', 'c11n_toggle-card__header', styles, className)}
        {...rest}
      >
        {typeof children === 'string' ? (
          <Heading level={5}>{children}</Heading>
        ) : (
          <Slottable>{children}</Slottable>
        )}
      </Component>
    );
  },
);

ToggleCardHeader.displayName = 'ToggleCard.Header';
